import React, { useEffect, useState } from 'react'
import { apiClient } from '@/utils/api'
import { CheckCircle, Clock, AlertCircle, XCircle, Loader2, CreditCard, Sparkles } from 'lucide-react'

interface SubscriptionStatus {
  id: string
  family_id: string
  product: string
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid'
  billing_cycle: 'monthly' | 'yearly'
  current_period_start: string
  current_period_end: string
  trial_end?: string
  cancel_at_period_end: boolean
  canceled_at?: string
  stripe_subscription_id: string
  created_at: string
  updated_at: string
}

interface StripeConfig {
  enabled: boolean
  publishable_key?: string
  price_id?: string
  monthly_price?: number
  yearly_price?: number
  currency?: string
}

// Subscribe Now component for users without a subscription
const SubscribeNow: React.FC<{ onSubscribed: () => void }> = ({ onSubscribed }) => {
  const [loading, setLoading] = useState(false)
  const [config, setConfig] = useState<StripeConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isDemo = typeof window !== 'undefined' && window.location.hostname.includes('demo')

  useEffect(() => {
    if (!isDemo) {
      loadConfig()
    }
  }, [])

  const loadConfig = async () => {
    try {
      const data = await apiClient.get('/api/subscriptions/config')
      setConfig(data)
    } catch (err) {
      console.error('Failed to load Stripe config:', err)
      setConfig({ enabled: false })
    }
  }

  const handleSubscribe = async () => {
    if (!config?.price_id) {
      setError('Subscription not available. Please contact support.')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { checkout_url } = await apiClient.createCheckoutSession({
        price_id: config.price_id,
        success_url: window.location.origin + '/dashboard/settings?subscription=success',
        cancel_url: window.location.origin + '/dashboard/settings?subscription=canceled'
      })

      window.location.href = checkout_url
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start checkout')
      setLoading(false)
    }
  }

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100)
  }

  // Demo family - show demo account info
  if (isDemo) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Demo Account</h3>
          <p className="text-gray-600">
            You're using HousePoints in demo mode with full access to all features.
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-green-800">Plan</span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle className="h-4 w-4" />
                Demo
              </span>
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p>All features are unlocked for evaluation.</p>
              <p>No payment information required.</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">
              Ready to set up HousePoints for your family?
            </p>
            <a
              href="https://housepoints.ai"
              className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
            >
              Get Started
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Subscribe to HousePoints</h3>
        <p className="text-gray-600">
          Continue using all features after your trial ends.
        </p>
      </div>

      {config?.enabled ? (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-900">
              {formatPrice(config.monthly_price || 499)}/month
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Cancel anytime
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Opening Checkout...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Subscribe Now
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500">
            Secure payment powered by Stripe
          </p>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Online subscription is coming soon. Please contact us for access.
          </p>
          <a
            href="https://housepoints.ai/contact"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
          >
            Contact Us
          </a>
        </div>
      )}
    </div>
  )
}

export const SubscriptionStatus: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      setLoading(true)
      const data = await apiClient.getSubscriptionStatus()
      // If the response has no meaningful data, treat as no subscription
      if (data && data.status) {
        setSubscription(data)
      } else {
        setSubscription(null)
      }
    } catch (err: any) {
      // Don't show error for missing subscription - just show SubscribeNow
      console.log('No subscription found:', err?.response?.status)
      setSubscription(null)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const configs = {
      active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      trialing: { label: 'Trial', color: 'bg-blue-100 text-blue-800', icon: Clock },
      past_due: { label: 'Past Due', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
      canceled: { label: 'Canceled', color: 'bg-red-100 text-red-800', icon: XCircle },
      incomplete: { label: 'Incomplete', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
      unpaid: { label: 'Unpaid', color: 'bg-red-100 text-red-800', icon: XCircle }
    }
    const config = configs[status as keyof typeof configs] || configs.incomplete
    const Icon = config.icon
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getDaysUntilRenewal = () => {
    if (!subscription) return null
    const end = new Date(subscription.current_period_end)
    const now = new Date()
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return diff
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-red-200">
        <div className="flex items-center gap-2 text-red-600 mb-2">
          <AlertCircle className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Error Loading Subscription</h3>
        </div>
        <p className="text-gray-600">{error}</p>
      </div>
    )
  }

  if (!subscription) {
    return <SubscribeNow onSubscribed={loadSubscription} />
  }

  const daysUntilRenewal = getDaysUntilRenewal()

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Current Subscription</h3>
        {getStatusBadge(subscription.status)}
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-600">Plan</label>
          <p className="text-lg text-gray-900 capitalize">{subscription.product}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Billing Cycle</label>
          <p className="text-lg text-gray-900 capitalize">{subscription.billing_cycle}</p>
        </div>

        {subscription.status === 'trialing' && subscription.trial_end && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="text-sm font-medium text-blue-900">Trial Ends</label>
            <p className="text-lg text-blue-900">{formatDate(subscription.trial_end)}</p>
          </div>
        )}

        {(subscription.status === 'active' || subscription.status === 'past_due') && (
          <div>
            <label className="text-sm font-medium text-gray-600">
              {subscription.cancel_at_period_end ? 'Subscription Ends' : 'Next Renewal'}
            </label>
            <p className="text-lg text-gray-900">{formatDate(subscription.current_period_end)}</p>
            {daysUntilRenewal !== null && daysUntilRenewal > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {daysUntilRenewal} day{daysUntilRenewal === 1 ? '' : 's'} remaining
              </p>
            )}
          </div>
        )}

        {subscription.cancel_at_period_end && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Your subscription will end on {formatDate(subscription.current_period_end)}.
              You can reactivate it anytime before then.
            </p>
          </div>
        )}

        {subscription.status === 'past_due' && daysUntilRenewal !== null && daysUntilRenewal < 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Your payment is past due. You have {Math.abs(daysUntilRenewal)} day{Math.abs(daysUntilRenewal) === 1 ? '' : 's'} remaining in your grace period.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
